import { useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { apiService } from '@/services/api'
import {
  EDITABLE_SUBMODULES,
  SUBMODULE_LABELS,
  readCanalproDraft,
  buildCanalproSavePayload,
  type ImoveisMode,
  type LeadsMode,
} from '@/features/tenants/canalproSubmodules'

interface Props {
  tenant: any
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}

/**
 * Drawer de configuração do CanalPro (aberto pelo card no Marketplace de Módulos).
 * - Modes (imóveis/leads) são seleção ÚNICA (XML xor GraphQL; Webhook xor GraphQL).
 * - Os submódulos mostram o estado configurado (override ou default herdado),
 *   mas só os ALTERADOS são enviados ao salvar (não materializa defaults — ver
 *   buildCanalproSavePayload).
 * - Submódulos ficam inertes enquanto o master (canalpro_enabled) estiver desligado.
 */
export function CanalProConfigDrawer({ tenant, open, onOpenChange, onSaved }: Props) {
  const caps: Record<string, any> = tenant?.capabilities_override || {}
  const masterEnabled = !!caps.canalpro_enabled

  // Estado inicial (no momento da abertura). useMemo por `tenant` p/ resincronizar
  // quando o pai recarrega o tenant após salvar.
  const initial = useMemo(() => readCanalproDraft(caps), [tenant])

  const [imoveisMode, setImoveisMode] = useState<ImoveisMode>(initial.imoveisMode)
  const [leadsMode, setLeadsMode] = useState<LeadsMode>(initial.leadsMode)
  const [leadsPollInterval, setLeadsPollInterval] = useState<number | null>(
    initial.leadsPollInterval ?? null,
  )
  const [subs, setSubs] = useState(initial.subs)
  const [saving, setSaving] = useState(false)

  // Resincroniza o estado local quando o tenant muda (ex.: reabrir após reload).
  const [syncKey, setSyncKey] = useState(tenant)
  if (syncKey !== tenant) {
    setSyncKey(tenant)
    setImoveisMode(initial.imoveisMode)
    setLeadsMode(initial.leadsMode)
    setLeadsPollInterval(initial.leadsPollInterval ?? null)
    setSubs(initial.subs)
  }

  const payload = buildCanalproSavePayload(initial, { imoveisMode, leadsMode, leadsPollInterval, subs })
  const hasChanges =
    Object.keys(payload.modes).length > 0 || Object.keys(payload.submodules).length > 0

  const handleSave = async () => {
    const tenantId = parseInt(tenant.id, 10)
    setSaving(true)
    let modesOk = true
    let subsOk = true
    try {
      if (Object.keys(payload.modes).length) {
        try {
          await apiService.updateTenantPortalModes(tenantId, payload.modes)
        } catch (e) {
          modesOk = false
          console.error('Falha ao salvar modes CanalPro', e)
        }
      }
      if (Object.keys(payload.submodules).length) {
        try {
          await apiService.updateTenantModules(tenantId, payload.submodules)
        } catch (e) {
          subsOk = false
          console.error('Falha ao salvar submódulos CanalPro', e)
        }
      }

      if (modesOk && subsOk) {
        toast.success('Configuração do CanalPro atualizada!')
        onSaved()
        onOpenChange(false)
      } else if (modesOk || subsOk) {
        // Salvamento parcial: parte persistiu. Recarrega p/ refletir o estado real.
        toast.error('Salvamento parcial: parte das mudanças não foi gravada. A tela foi recarregada com o estado atual.')
        onSaved()
        onOpenChange(false)
      } else {
        toast.error('Erro ao salvar a configuração do CanalPro.')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent aria-describedby={undefined}>
        <SheetHeader>
          <SheetTitle>Configurar CanalPro</SheetTitle>
          <SheetDescription>
            Modos de integração e submódulos deste tenant.{' '}
            {masterEnabled ? '' : 'Ligue o módulo CanalPro no card para ativar os submódulos.'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-6">
          <ModeSelector
            label="Imóveis"
            value={imoveisMode}
            options={[
              { value: 'xml', label: 'XML' },
              { value: 'graphql', label: 'GraphQL' },
            ]}
            onChange={(v) => setImoveisMode(v as ImoveisMode)}
          />
          <ModeSelector
            label="Leads"
            value={leadsMode}
            options={[
              { value: 'webhook', label: 'Webhook' },
              { value: 'graphql', label: 'GraphQL' },
            ]}
            onChange={(v) => setLeadsMode(v as LeadsMode)}
          />

          {leadsMode === 'graphql' && (
            <div>
              <p className="text-sm font-medium mb-2">Intervalo de busca de leads (minutos)</p>
              <Input
                type="number"
                min={1}
                max={60}
                placeholder="5"
                value={leadsPollInterval ?? ''}
                onChange={(e) =>
                  setLeadsPollInterval(e.target.value ? parseInt(e.target.value, 10) : null)
                }
              />
              <p className="text-xs text-muted-foreground mt-1">Padrão 5. Faixa permitida: 1 a 60.</p>
            </div>
          )}

          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-1">Submódulos</p>
            {!masterEnabled && (
              <p className="text-xs text-muted-foreground mb-3">
                Desabilitados enquanto o módulo CanalPro estiver desligado.
              </p>
            )}
            <p className="text-xs text-muted-foreground mb-3">
              Importação, Publicação e Leads seguem o modo acima (XML/Webhook ou GraphQL).
            </p>
            <div className="flex flex-col gap-3">
              {EDITABLE_SUBMODULES.map((name) => (
                <label key={name} className="flex items-center justify-between gap-3 text-sm">
                  <span>{SUBMODULE_LABELS[name]}{name === 'diagnostics' && <span className="block text-xs text-muted-foreground">Requer imóveis em modo GraphQL</span>}</span>
                  <Switch
                    aria-label={SUBMODULE_LABELS[name]}
                    checked={subs[name]}
                    disabled={!masterEnabled || (name === 'diagnostics' && imoveisMode !== 'graphql')}
                    onCheckedChange={(checked) =>
                      setSubs((prev) => ({ ...prev, [name]: checked }))
                    }
                  />
                </label>
              ))}
            </div>
          </div>
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Salvar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

function ModeSelector({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}) {
  return (
    <div>
      <p className="text-sm font-medium mb-2">{label}</p>
      <div className="inline-flex rounded-lg border p-1 gap-1">
        {options.map((opt) => {
          const active = opt.value === value
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={
                'px-3 py-1 text-sm rounded-md transition-colors ' +
                (active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted')
              }
              aria-pressed={active}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
